import React, { useEffect, useMemo, useState } from "react";
import { Modal, notification, Select, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import Button from "../../commons/Button/Button";
import { FormField } from "../../commons/FormField/FormField";
import { DropdownWithSearch } from "../../commons/Dropdown/DropdownWithSearch";
import Spinner from "../../commons/Spinner/Spinner";
import useSGTemplates from "../../state/hooks/sg-templates/useSGTemplates";
import useSendSGTemplateEmail from "../../state/hooks/sg-templates/useSendSGTemplateEmail";
import useUpdateSGTemplate from "../../state/hooks/sg-templates/useUpdateSGTemplate";
import {
  ISGTemplate,
  SGEmailType,
} from "../../models/Interface/SGTemplate.interface";
import { LanguageCodes } from "../../models/enum/language-codes.enum";

type TemplateInputMode = "select" | "paste";
type SendMode = "standard" | "mailreach_test";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_SENDER = "";
const DEFAULT_INTERFACE_LANGUAGE = LanguageCodes.EN;
const DEFAULT_TARGET_LANGUAGE = LanguageCodes.DE;

const LANGUAGE_OPTIONS = Object.values(LanguageCodes).map((code) => ({
  value: code,
  label: code,
}));

const EmailTypeBadge = ({ type }: { type?: SGEmailType | string }) => {
  if (!type) return <span>—</span>;

  const isTransactional = type === "transactional";

  return (
    <span
      className="inline-flex items-center rounded-md text-xs font-semibold capitalize whitespace-nowrap"
      style={{
        padding: "4px 10px",
        background: isTransactional ? "#2d3ac7" : "#EEF0F6",
        color: isTransactional ? "#fff" : "#3F4654",
        letterSpacing: "0.01em",
      }}
    >
      {type}
    </span>
  );
};

const EllipsisCell = ({
  value,
  mono = false,
  strong = false,
}: {
  value?: string;
  mono?: boolean;
  strong?: boolean;
}) => {
  if (!value) return <span style={{ color: "#9CA3AF" }}>—</span>;

  return (
    <Tooltip title={value} placement="topLeft">
      <span
        className={`block truncate text-sm ${strong ? "font-semibold text-dark-grey" : "text-dark-grey"}`}
        style={{
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
            : undefined,
          fontSize: mono ? 12 : undefined,
          maxWidth: "100%",
          lineHeight: 1.4,
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
};

const copyTemplateId = async (templateId: string) => {
  try {
    await navigator.clipboard.writeText(templateId);
    notification.success({
      key: "sg-template-id-copied",
      message: "Copied",
      description: templateId,
      placement: "top",
      duration: 2,
    });
  } catch {
    notification.error({
      message: "Copy Failed",
      description: "Could not copy template ID to clipboard.",
      placement: "top",
    });
  }
};

const CopyableTemplateId = ({
  value,
  truncate = false,
}: {
  value?: string;
  truncate?: boolean;
}) => {
  if (!value) return <span style={{ color: "#9CA3AF" }}>—</span>;

  return (
    <Tooltip title="Click to copy" placement="topLeft">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void copyTemplateId(value);
        }}
        className={`text-left text-dark-grey cursor-pointer ${
          truncate ? "block truncate w-full" : ""
        }`}
        style={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 12,
          lineHeight: 1.4,
          background: "transparent",
          border: "none",
          padding: 0,
          wordBreak: truncate ? undefined : "break-all",
          color: "#2d3ac7",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: 2,
        }}
      >
        {value}
      </button>
    </Tooltip>
  );
};

const SGTemplates = () => {
  const { data, isLoading, error } = useSGTemplates();
  const templates = data?.sgTemplates ?? [];

  const [sendMode, setSendMode] = useState<SendMode>("standard");
  const [templateMode, setTemplateMode] = useState<TemplateInputMode>("select");
  // Use record `id` (unique). Multiple rows can share the same SendGrid templateId.
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [pastedTemplateId, setPastedTemplateId] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [sender, setSender] = useState(DEFAULT_SENDER);
  const [interfaceLanguage, setInterfaceLanguage] = useState(
    DEFAULT_INTERFACE_LANGUAGE
  );
  const [targetLanguage, setTargetLanguage] = useState(DEFAULT_TARGET_LANGUAGE);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ISGTemplate | null>(
    null
  );
  const [editDescription, setEditDescription] = useState("");
  const isMailreachTest = sendMode === "mailreach_test";

  const { mutate: sendEmail, isPending } = useSendSGTemplateEmail();
  const { mutate: updateTemplate, isPending: isUpdating } =
    useUpdateSGTemplate();

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: t.id,
        label: `${t.templateName} · ${t.emailType} · ${t.account} · ${t.templateId}`,
      })),
    [templates]
  );

  const selectedFromList = templates.find((t) => t.id === selectedRecordId);

  const resolvedTemplateId =
    templateMode === "select"
      ? selectedFromList?.templateId?.trim() || ""
      : pastedTemplateId.trim();

  const selectedTemplate =
    templateMode === "select"
      ? selectedFromList
      : templates.find((t) => t.templateId === resolvedTemplateId);

  const isTemplateInUse = (record: ISGTemplate) => {
    if (templateMode === "select") {
      return record.id === selectedRecordId;
    }
    return !!resolvedTemplateId && record.templateId === resolvedTemplateId;
  };

  const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
  const canSubmit =
    !!resolvedTemplateId &&
    emails.length > 0 &&
    invalidEmails.length === 0 &&
    (!isMailreachTest ||
      (!!interfaceLanguage.trim() && !!targetLanguage.trim())) &&
    !isPending;

  const resetForm = () => {
    setSelectedRecordId(null);
    setPastedTemplateId("");
    setEmails([]);
    setSender(DEFAULT_SENDER);
    setTemplateMode("select");
    setSendMode("standard");
    setInterfaceLanguage(DEFAULT_INTERFACE_LANGUAGE);
    setTargetLanguage(DEFAULT_TARGET_LANGUAGE);
  };

  const handleUseTemplate = (template: ISGTemplate) => {
    setTemplateMode("select");
    setSelectedRecordId(template.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEditDescription = (template: ISGTemplate) => {
    setEditingTemplate(template);
    setEditDescription(template.description ?? "");
  };

  const closeEditDescription = () => {
    setEditingTemplate(null);
    setEditDescription("");
  };

  const handleUpdateDescription = () => {
    if (!editingTemplate) return;

    const description = editDescription.trim();
    if (description === (editingTemplate.description ?? "").trim()) {
      closeEditDescription();
      return;
    }

    updateTemplate(
      {
        id: editingTemplate.id,
        data: { description },
      },
      {
        onSuccess: (response) => {
          notification.success({
            message: "Description Updated",
            description:
              response.message || "Template description updated successfully.",
            placement: "top",
          });
          closeEditDescription();
        },
        onError: (err) => {
          notification.error({
            message: "Update Failed",
            description:
              err?.response?.data?.message ||
              "Failed to update template description.",
            placement: "top",
          });
        },
      }
    );
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    sendEmail(
      {
        templateId: resolvedTemplateId,
        emails,
        ...(sender.trim() ? { sender: sender.trim() } : {}),
        ...(isMailreachTest
          ? {
              isTest: true,
              interfaceLanguage: interfaceLanguage.trim(),
              targetLanguage: targetLanguage.trim(),
            }
          : {}),
      },
      {
        onSuccess: (response) => {
          notification.success({
            message: isMailreachTest ? "Test Emails Queued" : "Emails Queued",
            description: `${response.message} (queued: ${response.totalQueued})`,
            placement: "top",
          });
          resetForm();
        },
        onError: (err) => {
          notification.error({
            message: "Send Failed",
            description:
              err?.response?.data?.message ||
              "Failed to queue SendGrid template emails.",
            placement: "top",
          });
        },
      }
    );
  };

  const columns: ColumnsType<ISGTemplate> = [
    {
      title: "Template Name",
      dataIndex: "templateName",
      key: "templateName",
      width: 340,
      ellipsis: true,
      render: (name: string) => <EllipsisCell value={name} strong />,
    },
    {
      title: "Type",
      dataIndex: "emailType",
      key: "emailType",
      width: 140,
      filters: [
        { text: "Marketing", value: "marketing" },
        { text: "Transactional", value: "transactional" },
      ],
      onFilter: (value, record) => record.emailType === value,
      render: (type: SGEmailType) => <EmailTypeBadge type={type} />,
    },
    {
      title: "Account",
      dataIndex: "account",
      key: "account",
      width: 140,
      ellipsis: true,
      render: (account: string) => <EllipsisCell value={account} />,
    },
    {
      title: "Template ID",
      dataIndex: "templateId",
      key: "templateId",
      width: 280,
      ellipsis: true,
      render: (id: string) => <CopyableTemplateId value={id} truncate />,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      ellipsis: true,
      render: (description: string, record: ISGTemplate) => (
        <div className="flex items-center gap-10 min-w-0">
          <div className="min-w-0 flex-1" style={{ opacity: 0.75 }}>
            <EllipsisCell value={description} />
          </div>
          <button
            type="button"
            onClick={() => openEditDescription(record)}
            className="flex-shrink-0 text-xs font-semibold cursor-pointer"
            style={{
              color: "#2d3ac7",
              background: "transparent",
              border: "none",
              padding: 0,
            }}
          >
            Edit
          </button>
        </div>
      ),
    },
    {
      title: "Action",
      key: "actions",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_: unknown, record: ISGTemplate) => {
        const isInUse = isTemplateInUse(record);
        return (
          <Button
            type={isInUse ? "main" : "secondary"}
            border={!isInUse}
            height="20"
            paddingHorizontal="10"
            fontSize="sm"
            onClick={() => handleUseTemplate(record)}
          >
            Use
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (!error) return;
    notification.error({
      key: "sg-templates-load-error",
      message: "Error",
      description:
        error?.response?.data?.message || "Failed to load SendGrid templates.",
      placement: "top",
    });
  }, [error]);

  return (
    <div className="overflow-y-auto px-20 sm:px-30 pt-40 pb-30 bg-light-grey-1 h-full w-full min-w-0">
      <h2 className="text-dark-grey font-bold text-2xl mb-8">SG Management</h2>
      <p className="text-dark-grey mb-30 text-sm" style={{ opacity: 0.6 }}>
        Browse cached SendGrid templates and queue transactional emails to known
        users. Unknown recipients are skipped automatically.
      </p>

      <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 mb-30 w-full min-w-0">
        <h3 className="text-dark-grey font-bold text-lg mb-20">
          Send Template Email
        </h3>

        <FormField label="Send mode" col>
          <div className="inline-flex flex-wrap rounded-md border border-light-grey-3 border-solid overflow-hidden">
            <button
              type="button"
              onClick={() => setSendMode("standard")}
              className={`px-10 py-10 text-sm font-semibold transition-colors cursor-pointer ${
                sendMode === "standard"
                  ? "bg-primary text-white"
                  : "bg-white text-dark-grey"
              }`}
            >
              Standard
            </button>
            <button
              type="button"
              onClick={() => setSendMode("mailreach_test")}
              className={`px-10 py-10 text-sm font-semibold border-l border-light-grey-3 border-solid transition-colors cursor-pointer ${
                sendMode === "mailreach_test"
                  ? "bg-primary text-white"
                  : "bg-white text-dark-grey"
              }`}
            >
              Mailreach Test
            </button>
          </div>
          {isMailreachTest && (
            <p className="text-xs text-error mt-8">
              For Mailreach testing with recipients that do not exist in our
              system. Sends with isTest and language overrides.
            </p>
          )}
        </FormField>

        <FormField label="Template source" col>
          <div className="inline-flex flex-wrap rounded-md border border-light-grey-3 border-solid overflow-hidden">
            <button
              type="button"
              onClick={() => setTemplateMode("select")}
              className={`px-10 py-10 text-sm font-semibold transition-colors cursor-pointer ${
                templateMode === "select"
                  ? "bg-primary text-white"
                  : "bg-white text-dark-grey"
              }`}
            >
              Choose from list
            </button>
            <button
              type="button"
              onClick={() => setTemplateMode("paste")}
              className={`px-10 py-10 text-sm font-semibold border-l border-light-grey-3 border-solid transition-colors cursor-pointer ${
                templateMode === "paste"
                  ? "bg-primary text-white"
                  : "bg-white text-dark-grey"
              }`}
            >
              Paste template ID
            </button>
          </div>
        </FormField>

        {templateMode === "select" ? (
          <FormField label="Template" col>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Spinner />
                <span
                  className="text-sm text-dark-grey"
                  style={{ opacity: 0.5 }}
                >
                  Loading templates...
                </span>
              </div>
            ) : (
              <DropdownWithSearch
                options={templateOptions}
                placeholder="Search by name, type, account, or id..."
                value={selectedRecordId ?? undefined}
                onChange={(opt) => setSelectedRecordId(opt?.value ?? null)}
                className="w-full"
                id="sg-template-select"
              />
            )}
          </FormField>
        ) : (
          <FormField label="Template ID" col>
            <input
              type="text"
              value={pastedTemplateId}
              onChange={(e) => setPastedTemplateId(e.target.value)}
              placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full rounded-md border border-light-grey-3 px-4 py-3 text-sm text-dark-grey
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              id="sg-template-paste"
            />
            <p className="text-xs text-error mt-8">
              Only existing template IDs will be triggered — unknown ones will not
              be sent.
            </p>
          </FormField>
        )}

        {selectedTemplate && (
          <FormField label="Details" col>
            <div className="rounded-md bg-light-grey-1 px-20 py-10 text-sm text-dark-grey w-full flex flex-col gap-10">
              <div
                className="font-semibold"
                style={{ wordBreak: "break-word" }}
              >
                {selectedTemplate.templateName}
              </div>
              <div className="flex flex-wrap items-center gap-10">
                <EmailTypeBadge type={selectedTemplate.emailType} />
                <span style={{ opacity: 0.7 }}>{selectedTemplate.account}</span>
              </div>
              {selectedTemplate.description && (
                <div style={{ opacity: 0.7, wordBreak: "break-word" }}>
                  {selectedTemplate.description}
                </div>
              )}
              <CopyableTemplateId value={selectedTemplate.templateId} />
            </div>
          </FormField>
        )}

        <FormField label="Recipients" col>
          <Select
            mode="tags"
            value={emails}
            onChange={(values) =>
              setEmails(values.map((v) => String(v).trim()).filter(Boolean))
            }
            tokenSeparators={[",", " ", ";", "\n"]}
            placeholder="Type or paste emails, then press Enter"
            className="w-full"
            style={{ minHeight: 48 }}
            open={false}
            suffixIcon={null}
            id="sg-template-emails"
          />
          <p className="text-xs text-error mt-8">
            {isMailreachTest
              ? "Separate with Enter, comma, or space. Test mode allows recipients that do not exist in our system (Mailreach)."
              : "Separate with Enter, comma, or space. Only emails matching existing users/sub-users will be queued."}
          </p>
          {invalidEmails.length > 0 && (
            <p className="text-error text-sm mt-8">
              Invalid email{invalidEmails.length > 1 ? "s" : ""}:{" "}
              {invalidEmails.join(", ")}
            </p>
          )}
        </FormField>

        {isMailreachTest && (
          <>
            <FormField label="Interface language" col>
              <DropdownWithSearch
                options={LANGUAGE_OPTIONS}
                placeholder="Select interface language..."
                value={interfaceLanguage}
                onChange={(opt) =>
                  setInterfaceLanguage(
                    (opt?.value as LanguageCodes) ?? DEFAULT_INTERFACE_LANGUAGE
                  )
                }
                className="w-full"
                id="sg-interface-language"
              />
            </FormField>
            <FormField label="Target language" col>
              <DropdownWithSearch
                options={LANGUAGE_OPTIONS}
                placeholder="Select target language..."
                value={targetLanguage}
                onChange={(opt) =>
                  setTargetLanguage(
                    (opt?.value as LanguageCodes) ?? DEFAULT_TARGET_LANGUAGE
                  )
                }
                className="w-full"
                id="sg-target-language"
              />
            </FormField>
          </>
        )}

        <FormField label="Sender (optional)" col>
          <input
            type="text"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            placeholder="Talkpal AI"
            className="w-full rounded-md border border-light-grey-3 px-4 py-3 text-sm text-dark-grey
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            id="sg-template-sender"
          />
        </FormField>

        <div className="flex items-center gap-4 mt-20">
          <Button
            type="main"
            onClick={() => setConfirmVisible(true)}
            disabled={!canSubmit}
            isLoading={isPending}
          >
            Queue Emails
          </Button>
        </div>
      </div>

      <Modal
        title="Edit Description"
        open={!!editingTemplate}
        onCancel={closeEditDescription}
        footer={
          <div className="flex justify-end gap-10 mt-20">
            <Button
              type="secondary"
              border
              onClick={closeEditDescription}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="main"
              isLoading={isUpdating}
              onClick={handleUpdateDescription}
            >
              Save
            </Button>
          </div>
        }
      >
        {editingTemplate && (
          <div className="flex flex-col gap-20">
            <div>
              <div
                className="text-xs font-semibold mb-4"
                style={{ color: "#6b7280" }}
              >
                Template
              </div>
              <div
                className="text-sm font-semibold text-dark-grey"
                style={{ wordBreak: "break-word" }}
              >
                {editingTemplate.templateName}
              </div>
            </div>
            <div>
              <label
                htmlFor="sg-template-description"
                className="block text-xs font-semibold mb-8"
                style={{ color: "#6b7280" }}
              >
                Description
              </label>
              <textarea
                id="sg-template-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={4}
                placeholder="Enter description..."
                className="w-full rounded-md border border-light-grey-3 px-4 py-3 text-sm text-dark-grey
                  focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                  resize-y"
              />
              <p className="text-xs mt-8" style={{ color: "#6b7280" }}>
                Only the description can be updated.
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Confirm Send"
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <div className="flex justify-end gap-10 mt-20">
            <Button
              type="secondary"
              border
              onClick={() => setConfirmVisible(false)}
            >
              Cancel
            </Button>
            <Button
              type="main"
              isLoading={isPending}
              onClick={() => {
                setConfirmVisible(false);
                handleSubmit();
              }}
            >
              Confirm & Queue
            </Button>
          </div>
        }
      >
        <p className="text-sm" style={{ color: "#6b7280", marginBottom: 16 }}>
          {isMailreachTest
            ? "Review Mailreach test details before queueing."
            : "Review details before queueing. Unknown recipients will be skipped."}
        </p>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
          }}
        >
          <tbody>
            <tr>
              <td
                style={{ width: 140, verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Mode:
              </td>
              <td className="text-sm">
                {isMailreachTest ? "Mailreach Test" : "Standard"}
              </td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Template:
              </td>
              <td className="text-sm" style={{ wordBreak: "break-word" }}>
                {selectedTemplate?.templateName || resolvedTemplateId || "—"}
              </td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Email Type:
              </td>
              <td className="text-sm">
                {selectedTemplate?.emailType ? (
                  <EmailTypeBadge type={selectedTemplate.emailType} />
                ) : (
                  "—"
                )}
              </td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Template ID:
              </td>
              <td className="text-sm" style={{ wordBreak: "break-all" }}>
                {resolvedTemplateId || "—"}
              </td>
            </tr>
            {isMailreachTest && (
              <>
                <tr>
                  <td
                    style={{ verticalAlign: "top" }}
                    className="font-semibold text-dark-grey text-sm"
                  >
                    Interface lang:
                  </td>
                  <td className="text-sm">{interfaceLanguage || "—"}</td>
                </tr>
                <tr>
                  <td
                    style={{ verticalAlign: "top" }}
                    className="font-semibold text-dark-grey text-sm"
                  >
                    Target lang:
                  </td>
                  <td className="text-sm">{targetLanguage || "—"}</td>
                </tr>
              </>
            )}
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Sender:
              </td>
              <td className="text-sm">{sender.trim() || "—"}</td>
            </tr>
            <tr>
              <td
                style={{ verticalAlign: "top" }}
                className="font-semibold text-dark-grey text-sm"
              >
                Recipients:
              </td>
              <td className="text-sm" style={{ wordBreak: "break-word" }}>
                {emails.length} email{emails.length === 1 ? "" : "s"}
                <div style={{ color: "#6b7280", marginTop: 4 }}>
                  {emails.join(", ")}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </Modal>

      <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0 overflow-hidden">
        <div className="flex items-center justify-between mb-20 gap-10 flex-wrap">
          <h3 className="text-dark-grey font-bold text-lg">
            Available Templates
          </h3>
          {isLoading && <Spinner />}
        </div>

        {/* Card stack — small/medium screens */}
        <div className="flex flex-col gap-20 xl:hidden">
          {templates.length === 0 && !isLoading ? (
            <p className="text-sm text-dark-grey" style={{ opacity: 0.6 }}>
              No SendGrid templates found.
            </p>
          ) : (
            templates.map((template) => {
              const isInUse = isTemplateInUse(template);
              return (
                <div
                  key={template.id}
                  className="flex flex-col gap-10 rounded-lg border border-light-grey-3 border-solid p-20 min-w-0 bg-white"
                >
                  <div className="flex items-start justify-between gap-10">
                    <div className="min-w-0 flex-1 flex flex-col gap-10">
                      <div
                        className="font-semibold text-dark-grey text-sm"
                        style={{ wordBreak: "break-word", lineHeight: 1.35 }}
                        title={template.templateName}
                      >
                        {template.templateName}
                      </div>
                      <div className="flex flex-wrap items-center gap-10">
                        <EmailTypeBadge type={template.emailType} />
                        <span
                          className="text-xs font-semibold"
                          style={{ color: "#6b7280" }}
                        >
                          {template.account}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        type={isInUse ? "main" : "secondary"}
                        border={!isInUse}
                        height="20"
                        paddingHorizontal="10"
                        fontSize="sm"
                        onClick={() => handleUseTemplate(template)}
                      >
                        Use
                      </Button>
                    </div>
                  </div>

                  <div
                    className="rounded-md px-10 py-10"
                    style={{ background: "#F5F6FA" }}
                  >
                    <div
                      className="text-xs font-semibold mb-4"
                      style={{ color: "#6b7280" }}
                    >
                      Template ID
                    </div>
                    <CopyableTemplateId value={template.templateId} />
                  </div>

                  <div className="flex items-start justify-between gap-10">
                    <p
                      className="text-sm text-dark-grey m-0 flex-1"
                      style={{
                        opacity: 0.75,
                        wordBreak: "break-word",
                        lineHeight: 1.4,
                      }}
                    >
                      {template.description || "No description"}
                    </p>
                    <button
                      type="button"
                      onClick={() => openEditDescription(template)}
                      className="flex-shrink-0 text-xs font-semibold cursor-pointer"
                      style={{
                        color: "#2d3ac7",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Table — wide screens */}
        <div className="hidden xl:block w-full min-w-0 sg-templates-table">
          <Table<ISGTemplate>
            dataSource={templates}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            size="middle"
            tableLayout="fixed"
            scroll={{ x: 1260 }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100"],
              showTotal: (total) => `${total} templates`,
            }}
            locale={{ emptyText: "No SendGrid templates found." }}
          />
          <style>{`
            .sg-templates-table .ant-table {
              border: 1px solid #E6E8EF;
              border-radius: 10px;
              overflow: hidden;
            }
            .sg-templates-table .ant-table-thead > tr > th {
              background: #F5F6FA !important;
              color: #3F4654;
              font-weight: 700;
              font-size: 13px;
              padding: 12px 16px !important;
              border-bottom: 1px solid #E6E8EF !important;
              white-space: nowrap;
            }
            .sg-templates-table .ant-table-tbody > tr > td {
              padding: 10px 16px !important;
              border-bottom: 1px solid #EEF0F4 !important;
              vertical-align: middle;
              background: #fff !important;
            }
            .sg-templates-table .ant-table-tbody > tr:hover > td {
              background: #F8F9FC !important;
            }
            .sg-templates-table .ant-table-cell-fix-right {
              background: #fff !important;
              box-shadow: -4px 0 8px rgba(32, 34, 36, 0.04);
            }
            .sg-templates-table .ant-table-tbody > tr:hover .ant-table-cell-fix-right {
              background: #F8F9FC !important;
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default SGTemplates;
